import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';
import { NotificationDto, NotificationsClient } from '../../api';

/**
 * Own-inbox notifications (task 10.1's backend) for the header bell (task 10.2). A root
 * singleton — unlike feature facades (`PaymentsFacade`…) it backs a single app-shell fixture
 * (`AppNotificationBellComponent`), not a routed list page.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsFacade {
  private readonly client = inject(NotificationsClient);

  private readonly _items = signal<NotificationDto[]>([]);
  private readonly _loading = signal(false);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly unreadCount = computed(() => this._items().filter((item) => !item.isRead).length);

  /** Latest page of notifications (any read status) — enough for the bell's dropdown panel. */
  refresh(): void {
    this._loading.set(true);
    this.client
      .list(1, 20, undefined)
      .pipe(
        tap((result) => this._items.set(result.items)),
        catchError(() => {
          // The bell degrades silently — a failed refresh just leaves the previous state.
          return of(null);
        }),
      )
      .subscribe(() => this._loading.set(false));
  }

  markRead(id: string): void {
    this.client.markRead(id).subscribe(() => {
      // NotificationDto is a plain data holder here — mutate in place and return a new array
      // reference so the signal's equality check picks up the change.
      this._items.update((items) => {
        const match = items.find((item) => item.id === id);
        if (match) {
          match.isRead = true;
        }
        return [...items];
      });
    });
  }

  markAllRead(): void {
    this.client.markAllRead().subscribe(() => {
      this._items.update((items) => {
        items.forEach((item) => (item.isRead = true));
        return [...items];
      });
    });
  }
}
